import {
  Home,
  Users,
  MessageCircle,
  // FileText,
  Settings,
  Heart,
  Camera,
  // Mic,
  X,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  User,
  Bell,
  Mail,
} from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { NavLink } from "react-router-dom"
import { twMerge } from "tailwind-merge"
import React, { useMemo } from "react"
import { ReactComponent as LogoLarge } from "@assets/V2/t4ever-logo-large.svg"

import { useAuthStore, useUIStore } from "@store"

interface Props {
  isMobile: boolean
}

const Sidebar: React.FC<Props> = ({ isMobile }) => {
  const {
    sidebarOpen,
    setSidebarOpen,
    sidebarCollapsed,
    toggleSidebarCollapse,
    getUnreadNotificationsCount,
  } = useUIStore()
  const isEmailVerified = useAuthStore((state) => !!state.user?.email_verified_at)
  const isAdmin = useAuthStore((state) => state.isAdmin())
  const user = useAuthStore((state) => state.user)

  const navigation = useMemo(() => {
    const menuUsers = [
      { name: "Legacy Profiles", href: "/legacy-profiles", icon: Users },
      { name: "Questions", href: "/questions", icon: MessageCircle },
      // { name: "Conversations", href: "/conversations", icon: Mic },
      // { name: "Messages", href: "/messages", icon: FileText },
      { name: "Chat", href: "/chat", icon: Heart },
      { name: "Media", href: "/media", icon: Camera },
      {
        name: "Notifications",
        href: "/notifications",
        icon: Bell,
        badge: getUnreadNotificationsCount() > 0,
      },
      { name: "Email Inbox", href: "/email", icon: Mail },
    ]
    const menu = [
      { name: "Dashboard", href: "/dashboard", icon: Home },
      {
        name: "Settings",
        href: "/settings",
        icon: Settings,
        badge: !isAdmin && !isEmailVerified,
        IconBadge: ShieldAlert,
      },
    ]
    const menuSuperAdmin = [{ name: "Users", href: "/users", icon: Users }]
    if (isAdmin) {
      menu.splice(1, 0, ...menuSuperAdmin)
    } else {
      menu.splice(1, 0, ...(menuUsers as any))
    }
    return menu
  }, [isAdmin, isEmailVerified, getUnreadNotificationsCount])

  const getSidebarWidth = () => {
    if (isMobile) return "w-64"
    if (sidebarCollapsed) return "w-16"
    return "w-64"
  }

  const toggleCollapse = () => {
    if (!isMobile) {
      toggleSidebarCollapse()
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[99] lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isMobile ? (sidebarOpen ? 0 : "-100%") : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={twMerge(
          "fixed inset-y-0 left-0 z-[100] bg-white dark:bg-legacy-navy shadow-lg border-r border-legacy-light/20 dark:border-legacy-light/10 lg:translate-x-0 lg:!static lg:inset-0 transition-all duration-100",
          getSidebarWidth(),
        )}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-legacy-light/20 dark:border-legacy-light/10">
            <div className="flex items-center space-x-2 min-w-0">
              {!sidebarCollapsed && !isMobile && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="hidden lg:block"
                >
                  <div>
                    <LogoLarge
                      className={twMerge(
                        "transition-all duration-300",
                        sidebarCollapsed ? "h-6 w-6" : "h-7 w-auto",
                      )}
                    />
                  </div>
                </motion.div>
              )}
              {isMobile && (
                <LogoLarge
                  className={twMerge(
                    "transition-all duration-300",
                    sidebarCollapsed ? "h-6 w-6" : "h-7 w-auto",
                  )}
                />
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-lg hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 transition-colors duration-200 lg:hidden"
            >
              <X className="h-5 w-5 text-legacy-dark dark:text-legacy-cream" />
            </button>
            {!isMobile && (
              <button
                onClick={toggleCollapse}
                className="hidden lg:flex p-1 rounded-lg hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 transition-colors duration-200"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-legacy-dark dark:text-legacy-cream" />
                ) : (
                  <ChevronLeft className="h-4 w-4 text-legacy-dark dark:text-legacy-cream" />
                )}
              </button>
            )}
          </div>

          {user && !sidebarCollapsed && (
            <div className="px-4 py-3 border-b border-legacy-light/20 dark:border-legacy-light/10">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gradient-to-br from-legacy-blue to-legacy-navy rounded-full flex items-center justify-center">
                  {user.avatar_path ? (
                    <img
                      src={user.avatar_path || "/default-avatar.png"}
                      className="h-8 w-8 rounded-full"
                      alt=""
                    />
                  ) : (
                    <User className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-legacy-dark dark:text-legacy-cream truncate">
                    {user.name || user.email}
                  </p>
                  <p className="text-xs text-legacy-light dark:text-legacy-light/70 truncate select-all">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon
              const IconBadge = item.IconBadge
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => isMobile && setSidebarOpen(false)}
                  className={({ isActive }) =>
                    twMerge(
                      "group flex items-center rounded-lg transition-all duration-200 relative",
                      sidebarCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5 space-x-3",
                      isActive
                        ? "bg-legacy-blue text-white shadow-md"
                        : "text-legacy-dark dark:text-legacy-cream hover:bg-legacy-light/20 dark:hover:bg-legacy-light/10 hover:shadow-sm",
                    )
                  }
                >
                  <Icon
                    className={twMerge(
                      "transition-all duration-200",
                      sidebarCollapsed ? "h-5 w-5" : "h-5 w-5",
                    )}
                  />

                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: "auto" }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap overflow-hidden"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {item.badge && !!IconBadge && (
                    <span
                      className={twMerge(
                        "flex items-center justify-center rounded-full",
                        sidebarCollapsed ? "absolute -top-1 -right-1 w-4 h-4" : "ml-auto w-5 h-5",
                      )}
                    >
                      <IconBadge size={sidebarCollapsed ? 12 : 16} strokeWidth={3} className="text-red-400" />
                    </span>
                  )}

                  {/* Tooltip for collapsed state */}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-2 px-3 py-2 bg-legacy-dark dark:bg-legacy-navy text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                      {item.badge && (
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-legacy-dark dark:bg-legacy-navy rotate-45"></div>
                      )}
                    </div>
                  )}
                </NavLink>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-legacy-light/20 dark:border-legacy-light/10">
            <div
              className={twMerge(
                "text-xs text-legacy-light dark:text-legacy-light/70 transition-all duration-200",
                sidebarCollapsed ? "text-center" : "text-center",
              )}
            >
              <AnimatePresence mode="wait">
                {sidebarCollapsed ? (
                  <motion.div
                    key="collapsed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-center"
                  >
                    v1.0.0
                  </motion.div>
                ) : (
                  <motion.div
                    key="expanded"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-center"
                  >
                    Legacy AI v1.0.0
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  )
}

export default Sidebar
